// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { ActivityIndicator, Image, Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import GoffyInput from '@/components/GoffyInput';
import { Text } from '@/components/Themed';
import appColors from '@/theme/colors';
import { MAX_TOUR_NAME_LENGTH } from '@/services/tourName';
import { getTourImageUri } from '@/services/tourImageSource';
import { getAssetImageSource } from '@/services/assetImageSource';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import SectionHeading from '../shared/SectionHeading';
import EditorCard from '../shared/EditorCard';
import { TOUR_EDIT_MEDIA_PAGE_PATH } from '../../constants';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useCallback, useMemo, useRef } from 'react';
import type { PickedTourImage, TourImageUploadPhase } from '../../types';

type Props = {
  tourName: string;
  onNameChange: (value: string) => void;
  onSave: (reason: string) => void;
  saving: boolean;
  tourId: string;
  imageKey: string | null;
  imagePreviewUri: string | null;
  uploadPhase: TourImageUploadPhase;
  onUploadImage: (picked: PickedTourImage) => void;
};

export default function TourNameEditor({
  tourName,
  onNameChange,
  onSave,
  saving,
  tourId,
  imageKey,
  imagePreviewUri,
  uploadPhase,
  onUploadImage,
}: Props) {
  const imageUploading = uploadPhase !== 'idle';
  const lastSavedRef = useRef(tourName);
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const serverImageUri = getTourImageUri(apiBaseUrl, tourId, imageKey);
  // The freshly-picked file wins until the server confirms the upload.
  const imageUri = imagePreviewUri ?? serverImageUri;
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleWebFile = useCallback(
    async (event: { target: HTMLInputElement }) => {
      const file = event.target.files?.[0];
      // Reset so picking the same file twice still fires a change event.
      event.target.value = '';
      if (!file) return;
      onUploadImage({
        blob: file,
        contentType: file.type || 'image/jpeg',
        fileName: file.name || `tour-${tourId}.jpg`,
        previewUri: URL.createObjectURL(file),
      });
    },
    [onUploadImage, tourId]
  );

  const handleNativePick = useCallback(async () => {
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) return;
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      quality: 0.9,
    });
    if (result.canceled || result.assets.length === 0) return;
    const asset = result.assets[0];
    const blob = await (await fetch(asset.uri)).blob();
    onUploadImage({
      blob,
      contentType: blob.type || 'image/jpeg',
      fileName: asset.fileName ?? `tour-${tourId}.jpg`,
      previewUri: asset.uri,
    });
  }, [onUploadImage, tourId]);

  // expo-image-picker's launchImageLibraryAsync is a no-op on web — it resolves
  // nothing and no dialog opens — so web goes through a real file input, the
  // same split app/profile uses for the player avatar.
  const handlePickPress = useCallback(() => {
    if (imageUploading) return;
    if (Platform.OS === 'web') {
      fileInputRef.current?.click();
    } else {
      void handleNativePick();
    }
  }, [handleNativePick, imageUploading]);
  // Same placeholder the tour cards fall back to, so a tour without its own
  // picture looks the same here as it does in the list.
  const defaultTourImage = useMemo(
    () => getAssetImageSource('images/trophy.png', 'icon', TOUR_EDIT_MEDIA_PAGE_PATH),
    []
  );

  useAutoSave(tourName, lastSavedRef.current, () => {
    lastSavedRef.current = tourName.trim();
    onSave('tour.name.autosave');
  });

  return (
    <View style={styles.container}>
      <EditorCard>
        <SectionHeading title="Tour Name" hint="Shown on the tour card and the leaderboard." />
        <GoffyInput
          testID="tour-edit-name-input"
          style={styles.input}
          value={tourName}
          onChangeText={onNameChange}
          placeholder="Enter tour name..."
          maxLength={MAX_TOUR_NAME_LENGTH}
        />
        {saving && <Text testID="tour-edit-saving" style={styles.savingText}>Saving...</Text>}
      </EditorCard>

      <EditorCard>
        <SectionHeading title="Tour Image" hint="Optional. Helps you tell your tours apart." />
        <View style={styles.imageRow}>
          <View style={styles.imageFrame}>
            <Image
              source={imageUri ? { uri: imageUri } : defaultTourImage}
              style={[styles.image, imageUploading && styles.imagePending]}
              // The placeholder is a trophy on a transparent background, so contain
              // it rather than cropping like a real cover photo.
              resizeMode={imageUri ? 'cover' : 'contain'}
            />
            {/* Dim the preview so it reads as "not saved yet" rather than done. */}
            {imageUploading ? (
              <View style={styles.imageFrameOverlay}>
                <ActivityIndicator size="small" color={appColors.accent.primary} />
              </View>
            ) : null}
          </View>
          <Pressable
            style={[styles.imageButton, imageUploading && styles.imageButtonBusy]}
            onPress={handlePickPress}
            disabled={imageUploading}
          >
            {imageUploading ? (
              <View style={styles.imageButtonBusyRow}>
                <ActivityIndicator size="small" color={appColors.accent.primary} />
                {/* Naming the step matters: uploading the bytes can take a while
                    on a phone connection, and a bare spinner looks stuck. */}
                <Text style={styles.imageButtonText}>
                  {uploadPhase === 'saving' ? 'Saving…' : 'Uploading…'}
                </Text>
              </View>
            ) : (
              <Text style={styles.imageButtonText}>{imageUri ? 'Change image' : 'Add image'}</Text>
            )}
          </Pressable>
        </View>
        {Platform.OS === 'web' ? (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(event) => void handleWebFile(event as unknown as { target: HTMLInputElement })}
          />
        ) : null}
      </EditorCard>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  // Frame and fill come from GoffyInput; the tour name is deliberately larger and bolder.
  input: {
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    fontWeight: '600',
  },
  savingText: {
    fontSize: 12,
    color: appColors.text.muted,
    fontStyle: 'italic',
  },
  imageRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  imageFrame: {
    width: 96,
    height: 96,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.surface.input,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  imageButton: {
    minWidth: 130,
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: appColors.accent.subtleBorder,
    backgroundColor: appColors.accent.subtleBg,
  },
  imageButtonBusy: {
    opacity: 0.85,
  },
  imageButtonBusyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  imagePending: {
    opacity: 0.45,
  },
  imageFrameOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 13,
  },
});
